import { useEffect, useMemo } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { SidebarItem } from '@backstage/core-components';
import { Avatar } from '@backstage/ui';
import {
  errorApiRef,
  identityApiRef,
  useApi,
  useRouteRef,
  type IconComponent,
} from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityPresentationApiRef,
  entityRouteParams,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { SidebarNavGroup } from './SidebarNavGroup';

/** plugin-org's MyGroupsSidebarItem as a SidebarNavGroup: same query, one link for a single group. */
export function MyGroupsNavGroup({ icon }: { icon: IconComponent }) {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const presentationApi = useApi(entityPresentationApiRef);
  const errorApi = useApi(errorApiRef);
  const entityRoute = useRouteRef(entityRouteRef);

  const { value: groups, error } = useAsync(async () => {
    const { userEntityRef } = await identityApi.getBackstageIdentity();
    const { items } = await catalogApi.getEntities({
      filter: [{ kind: 'group', 'relations.hasMember': userEntityRef }],
      fields: ['metadata', 'kind', 'spec.profile'],
    });
    return items;
  }, [identityApi, catalogApi]);

  useEffect(() => {
    if (error) errorApi.post(error);
  }, [error, errorApi]);

  // entityRouteParams lowercases the kind, which the case-sensitive active match needs.
  const items = useMemo(
    () =>
      (groups ?? []).map(group => {
        const label = presentationApi.forEntity(group).snapshot.primaryTitle;
        const picture = (
          group.spec?.profile as { picture?: string } | undefined
        )?.picture;
        return {
          id: stringifyEntityRef(group),
          label,
          href: entityRoute(entityRouteParams(group, { encodeParams: true })),
          icon: <Avatar size="x-small" name={label} src={picture ?? ''} />,
        };
      }),
    [groups, presentationApi, entityRoute],
  );

  if (!items.length) return null;
  if (items.length === 1) {
    return <SidebarItem icon={icon} to={items[0].href} text="My Group" />;
  }
  return <SidebarNavGroup icon={icon} text="My Groups" items={items} />;
}
